import { useCallback, useEffect, useRef, useState } from 'react'
import type { Call, Device } from '@twilio/voice-sdk'
import { supabase } from '@/lib/supabase/client'

export type SoftphoneStatus = 'idle' | 'connecting' | 'ready' | 'incoming' | 'on-call' | 'error' | 'offline'

interface SoftphoneState {
  status: SoftphoneStatus
  incomingFrom: string | null
  activeNumber: string | null
  muted: boolean
  error: string | null
}

async function fetchToken(): Promise<string | null> {
  const { data, error } = await supabase.functions.invoke('twilio-token', { body: {} })
  if (error) throw error
  if ((data as any)?.error) throw new Error((data as any).error)
  return (data as any)?.token ?? null
}

/**
 * Browser softphone over the Twilio Voice SDK. The SDK is dynamically
 * imported so it never lands in the main bundle and a load/registration
 * failure degrades gracefully (status 'error'/'offline') instead of
 * crashing the app. While ready, it heartbeats presence so inbound calls
 * ring this dispatcher (ring-all). See docs/SERVICE_INTAKE_TWILIO.md.
 */
export function useSoftphone(enabled: boolean) {
  const [state, setState] = useState<SoftphoneState>({
    status: 'idle', incomingFrom: null, activeNumber: null, muted: false, error: null,
  })
  const deviceRef = useRef<Device | null>(null)
  const incomingRef = useRef<Call | null>(null)
  const activeRef = useRef<Call | null>(null)

  const patch = useCallback((p: Partial<SoftphoneState>) => setState((s) => ({ ...s, ...p })), [])

  // Wire a Call's lifecycle into state.
  const bindCall = useCallback((call: Call, number: string | null) => {
    call.on('accept', () => { activeRef.current = call; patch({ status: 'on-call', activeNumber: number, muted: false }) })
    call.on('disconnect', () => {
      activeRef.current = null; incomingRef.current = null
      patch({ status: 'ready', activeNumber: null, incomingFrom: null, muted: false })
    })
    call.on('cancel', () => { incomingRef.current = null; patch({ status: 'ready', incomingFrom: null }) })
    call.on('reject', () => { incomingRef.current = null; patch({ status: 'ready', incomingFrom: null }) })
    call.on('error', (e: any) => { patch({ status: 'ready', error: e?.message ?? 'Call error' }) })
  }, [patch])

  // Set up / tear down the Device.
  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    let presenceTimer: ReturnType<typeof setInterval> | null = null

    ;(async () => {
      try {
        patch({ status: 'connecting', error: null })
        const token = await fetchToken()
        if (!token) { patch({ status: 'offline', error: 'Calling not configured' }); return }
        const { Device } = await import('@twilio/voice-sdk')
        if (cancelled) return

        const device = new Device(token, { codecPreferences: ['opus', 'pcmu'] as any, logLevel: 'error' as any })
        deviceRef.current = device

        device.on('registered', () => patch({ status: 'ready' }))
        device.on('unregistered', () => patch({ status: 'offline' }))
        device.on('error', (e: any) => patch({ status: 'error', error: e?.message ?? 'Device error' }))
        device.on('incoming', (call: Call) => {
          incomingRef.current = call
          bindCall(call, call.parameters?.From ?? null)
          patch({ status: 'incoming', incomingFrom: call.parameters?.From ?? 'Unknown' })
        })
        device.on('tokenWillExpire', async () => {
          try { const t = await fetchToken(); if (t) device.updateToken(t) } catch { /* ignore */ }
        })

        await device.register()

        // Presence heartbeat (so inbound calls ring this browser).
        const beat = () => { supabase.rpc('upsert_service_intake_presence').then(() => {}, () => {}) }
        beat()
        presenceTimer = setInterval(beat, 15000)
      } catch (e) {
        if (!cancelled) patch({ status: 'error', error: e instanceof Error ? e.message : 'Softphone init failed' })
      }
    })()

    return () => {
      cancelled = true
      if (presenceTimer) clearInterval(presenceTimer)
      try { activeRef.current?.disconnect() } catch { /* ignore */ }
      try { deviceRef.current?.destroy() } catch { /* ignore */ }
      deviceRef.current = null
      activeRef.current = null
      incomingRef.current = null
    }
  }, [enabled, patch, bindCall])

  const answer = useCallback(() => {
    const call = incomingRef.current
    if (call) { call.accept(); }
  }, [])

  const decline = useCallback(() => {
    const call = incomingRef.current
    if (call) { call.reject(); incomingRef.current = null; patch({ status: 'ready', incomingFrom: null }) }
  }, [patch])

  const hangup = useCallback(() => {
    try { activeRef.current?.disconnect() } catch { /* ignore */ }
    try { deviceRef.current?.disconnectAll() } catch { /* ignore */ }
  }, [])

  const dial = useCallback(async (target: string) => {
    const device = deviceRef.current
    if (!device) return
    try {
      patch({ status: 'connecting', activeNumber: target })
      const call = await device.connect({ params: { target } })
      bindCall(call, target)
    } catch (e) {
      patch({ status: 'ready', error: e instanceof Error ? e.message : 'Could not place call' })
    }
  }, [patch, bindCall])

  const toggleMute = useCallback(() => {
    const call = activeRef.current
    if (!call) return
    const next = !state.muted
    try { call.mute(next); patch({ muted: next }) } catch { /* ignore */ }
  }, [state.muted, patch])

  return { ...state, answer, decline, hangup, dial, toggleMute }
}
